'use client'
import { base_list_url, role_list_url, user_url } from '@/config/endpoints'
import { errorHandler } from '@/config/errorReporting'
import { objectToQueryParams, removeEmptyValuesFromObject } from '@/utils/functions'
import { IUserBase, IUserRole } from '../domain/IUser'
import { IUserFilters } from '../domain/UsersTable'
import { User } from 'firebase/auth'
import { getFirebaseTokenFromCookies } from "@/modules/users/service/cookies";

export async function fetchUsers({
	filters,
	page,
	currentUser,
}: {
	filters: IUserFilters;
	page: number;
	currentUser: User}) {
	// CSR
	const firebaseToken = await currentUser.getIdToken(true);

	try {
		const headers = {
			Authorization: `Bearer ${firebaseToken}`,
		};
		const queryParams = objectToQueryParams({
			...removeEmptyValuesFromObject({ ...filters, page }),
		});

		const users = await fetch(`${user_url}/usersList?${queryParams}`, {
			cache: "no-store",
			headers,
		});
		if (!users.ok) throw new Error(users.statusText);

		return await users.json();
	} catch (err) {
		errorHandler?.report(`Error en request de obtener usuarios: ${err}`);
		throw err;
	}
}

export async function getRoleList(): Promise<IUserRole[]> {
  // CSR
  try{
    const firebaseToken = await getFirebaseTokenFromCookies()
    const headers = {
      'Authorization': `Bearer ${firebaseToken}`
    }
    const roles = await fetch(role_list_url, { cache: 'no-store', headers })
    if(!roles.ok) throw new Error(roles.statusText)

    return await roles.json()
  } catch (err) {
    errorHandler?.report(`Error en request de obtener lista de roles: ${err}`);
    throw err;
  }
}

export async function addUserRole(uid: string, role: number): Promise<void> {
	const firebaseToken = await getFirebaseTokenFromCookies();
	const headers = {
		Authorization: `Bearer ${firebaseToken}`,
		'Content-Type': 'application/json',
	};
	const res = await fetch(`${user_url}/${uid}/role`, {
		method: 'POST',
		headers,
		body: JSON.stringify({ role }),
	});
	if (!res.ok) throw new Error(res.statusText);
}

/**
 * Reemplaza las líneas de un supervisor de línea. La API valida que tenga el
 * rol y una base, y que cada línea exista; el mensaje de error es para
 * mostrarlo tal cual.
 */
export async function setUserLineas(uid: string, lineas: string[]): Promise<string[]> {
	const firebaseToken = await getFirebaseTokenFromCookies();
	const res = await fetch(`${user_url}/${uid}/lineas`, {
		method: 'PUT',
		headers: {
			Authorization: `Bearer ${firebaseToken}`,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ lineas }),
	});
	const cuerpo = await res.json().catch(() => null);
	if (!res.ok) throw new Error(cuerpo?.message || res.statusText);
	return cuerpo.lineas;
}

export async function removeUserRole(uid: string, role: number): Promise<void> {
	const firebaseToken = await getFirebaseTokenFromCookies();
	const headers = {
		Authorization: `Bearer ${firebaseToken}`,
		'Content-Type': 'application/json',
	};
	const res = await fetch(`${user_url}/${uid}/role`, {
		method: 'DELETE',
		headers,
		body: JSON.stringify({ role }),
	});
	if (!res.ok) throw new Error(res.statusText);
}

export async function getBaseList({
	currentUser,
}: {
	currentUser: User;
}): Promise<IUserBase[]> {
	// CSR
	try {
		const firebaseToken = await currentUser?.getIdToken(true);
		const headers = {
			Authorization: `Bearer ${firebaseToken}`,
		};
		const bases = await fetch(base_list_url, {
			cache: "no-store",
			headers,
		});
		if (!bases.ok) throw new Error(bases.statusText);

		return await bases.json();
	} catch (err) {
		errorHandler?.report(
			`Error en request de obtener lista de bases: ${err}`
		);
		throw err;
	}
}


/**
 * Da de baja o rehabilita a un usuario.
 *
 * Es baja lógica, no borrado: eliminar al usuario borraría en cascada todas sus
 * evaluaciones, porque Operation.user_id está en CASCADE.
 */
export async function setUserActive(uid: string, active: boolean) {
	const firebaseToken = await getFirebaseTokenFromCookies();
	const response = await fetch(`${user_url}/${uid}/active`, {
		method: "PATCH",
		headers: {
			Authorization: `Bearer ${firebaseToken}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ active }),
	});
	if (!response.ok) {
		throw new Error(
			`No se pudo ${active ? "rehabilitar" : "dar de baja"} al usuario`,
		);
	}
	return response.json();
}

/** Campos editables de una persona. Sólo se manda lo que cambió. */
export type IUserEditableFields = {
	firstName?: string;
	lastName?: string;
	nomina?: string;
	sex?: string;
	dob?: string;
	phone?: string | null;
	documentType?: string;
	documentNumber?: string;
	position?: string;
	base?: number;
	padron?: "trenes" | "emergencias";
};

/**
 * Edita los datos de una persona.
 *
 * Sólo ADMIN, OWNER y SOPORTE: la API devuelve 403 para el resto.
 */
export async function updateUser(uid: string, data: IUserEditableFields) {
	const firebaseToken = await getFirebaseTokenFromCookies();
	const response = await fetch(`${user_url}/${uid}`, {
		method: "PATCH",
		headers: {
			Authorization: `Bearer ${firebaseToken}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) {
		const detalle = await response.text().catch(() => "");
		throw new Error(
			response.status === 403
				? "Tu perfil no puede editar datos de personas"
				: `No se pudieron guardar los cambios. ${detalle}`,
		);
	}
	return response.json();
}
